import * as React from 'react'

import { HEX_COLOR } from '@/lib/validators'

import { OPTIONAL_BRAND_COLOR_KEYS } from '@/brand/color-keys'
import { MARCO_BRAND } from '@/brand/marco'
import type { Brand, BrandTokens } from '@/brand/types'

type BrandProviderProps = {
  children: React.ReactNode
  defaultBrand?: Brand
}

type BrandContextValue = {
  brand: Brand
  setBrand: (brand: Brand) => void
  setPreviewBrand: (brand: Brand | null) => void
}

const BrandContext = React.createContext<BrandContextValue | undefined>(
  undefined,
)

// `Record<keyof BrandTokens, string>` so TS catches a missing entry when
// BrandTokens grows. Keep `--brand-*` values aligned with src/shared/styles/tokens.css.
const BRAND_VAR_MAP: Record<keyof BrandTokens, string> = {
  primary: '--brand-primary',
  primaryFg: '--brand-primary-fg',
  accent: '--brand-accent',
  accentFg: '--brand-accent-fg',
  surface: '--brand-surface',
  sidebar: '--brand-sidebar',
  primaryDark: '--brand-primary-dark',
  primaryFgDark: '--brand-primary-fg-dark',
  accentDark: '--brand-accent-dark',
  accentFgDark: '--brand-accent-fg-dark',
  surfaceDark: '--brand-surface-dark',
  sidebarDark: '--brand-sidebar-dark',
  logoUrlLight: '--brand-logo-url-light',
  logoUrlDark: '--brand-logo-url-dark',
  logoAspect: '--brand-logo-aspect',
  fontHeading: '--brand-font-heading',
  fontBody: '--brand-font-body',
}

const URL_FIELDS = new Set<keyof BrandTokens>(['logoUrlLight', 'logoUrlDark'])

/* eslint-disable no-control-regex */
const SAFE_PATH_URL = /^\/(?!\/)[^"')\s\\\x00-\x1f\x7f]*$/
const SAFE_HTTPS_URL = /^https:\/\/[^"')\s\\\x00-\x1f\x7f]+$/i
const UNSAFE_VALUE = /[;{}<>\\()\x00-\x1f\x7f]|expression|@import|\/\*/i
/* eslint-enable no-control-regex */
const MAX_VALUE_LENGTH = 2048

function isSafe(field: keyof BrandTokens, raw: string): boolean {
  if (OPTIONAL_BRAND_COLOR_KEYS.has(field)) return raw === '' || HEX_COLOR.test(raw)
  if (raw.trim().length === 0 || raw.length > MAX_VALUE_LENGTH) return false
  return URL_FIELDS.has(field)
    ? SAFE_PATH_URL.test(raw) || SAFE_HTTPS_URL.test(raw)
    : !UNSAFE_VALUE.test(raw)
}

function applyBrand(brand: Brand) {
  const fields = Object.keys(BRAND_VAR_MAP) as Array<keyof BrandTokens>
  const unsafeFields = fields.filter((field) => {
    const raw = brand[field]
    if (OPTIONAL_BRAND_COLOR_KEYS.has(field) && raw === undefined) return false
    return typeof raw !== 'string' || !isSafe(field, raw)
  })
  if (unsafeFields.length > 0) {
    console.warn(
      `BrandProvider: unsafe value(s) for ${unsafeFields.join(', ')} — applying the Marco default`,
    )
  }
  const applied = unsafeFields.length === 0 ? brand : MARCO_BRAND
  const root = document.documentElement
  for (const field of fields) {
    const raw = applied[field]
    if (raw === undefined || raw === '') {
      root.style.removeProperty(BRAND_VAR_MAP[field])
      continue
    }
    const value = URL_FIELDS.has(field) ? `url("${raw}")` : raw
    root.style.setProperty(BRAND_VAR_MAP[field], value)
  }
  applySidebar(root, applied.sidebar, '')
  applySidebar(root, applied.sidebarDark || applied.sidebar, '-dark')
}

function applySidebar(root: HTMLElement, color: string | undefined, suffix: string) {
  if (color) {
    const foreground = sidebarForeground(color)
    const accent = sidebarAccent(color, foreground)
    root.style.setProperty(`--brand-sidebar-foreground${suffix}`, foreground)
    root.style.setProperty(`--brand-sidebar-accent${suffix}`, accent)
    root.style.setProperty(`--brand-sidebar-accent-foreground${suffix}`, sidebarForeground(accent))
    root.style.setProperty(`--brand-sidebar-logo-filter${suffix}`, foreground === '#000000' ? 'brightness(0)' : 'brightness(0) invert(1)')
  } else {
    for (const variable of ['foreground', 'accent', 'accent-foreground', 'logo-filter']) {
      root.style.removeProperty(`--brand-sidebar-${variable}${suffix}`)
    }
  }
}

function sidebarForeground(color: string) {
  const [red = 0, green = 0, blue = 0] = [color.slice(1, 3), color.slice(3, 5), color.slice(5, 7)].map((hex) => {
    const channel = Number.parseInt(hex, 16) / 255
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue
  return luminance > 0.179 ? '#000000' : '#FFFFFF'
}

function sidebarAccent(color: string, foreground: string) {
  const overlay = foreground === '#FFFFFF' ? 255 : 0
  return '#' + [1, 3, 5].map((offset) => {
    const channel = Number.parseInt(color.slice(offset, offset + 2), 16)
    return Math.round(channel * 0.84 + overlay * 0.16).toString(16).padStart(2, '0')
  }).join('')
}

export const __isSafeForTests = isSafe

export function BrandProvider({
  children,
  defaultBrand = MARCO_BRAND,
}: BrandProviderProps) {
  const [brand, setBrand] = React.useState<Brand>(defaultBrand)
  const [previewBrand, setPreviewBrand] = React.useState<Brand | null>(null)

  React.useLayoutEffect(() => {
    applyBrand(previewBrand ?? brand)
  }, [previewBrand, brand])

  return (
    <BrandContext.Provider value={{ brand, setBrand, setPreviewBrand }}>
      {children}
    </BrandContext.Provider>
  )
}

export function useBrand() {
  const context = React.useContext(BrandContext)
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider')
  }
  return context
}
